import { Button } from '@/components/ui/Button'
import { auth } from '@/lib/auth'
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, CheckCircle, Circle, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/auth/success')({
  component: AuthSuccess,
})

// 진행 단계 정의
const STEPS = [
  { id: 1, label: '로그인 확인' },
  { id: 2, label: 'Cafe24 연동' },
  { id: 3, label: '대시보드 준비' },
]

// 진행 중 표시할 메시지들
const LOADING_MESSAGES = [
  '상점 정보를 확인하고 있어요',
  '설정을 불러오는 중이에요',
  '거의 다 됐어요!',
]

function AuthSuccess() {
  const [error, setError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0])

  useEffect(() => {
    const handleAuthSuccess = () => {
      try {
        // URL에서 토큰 추출
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token') || urlParams.get('access_token')

        if (token) {
          auth.setToken(token)
        } else {
          // URL fragment 확인 (#access_token=...)
          const fragment = window.location.hash.substring(1)
          const fragmentParams = new URLSearchParams(fragment)
          const fragmentToken = fragmentParams.get('access_token')

          if (fragmentToken) {
            auth.setToken(fragmentToken)
          }
        }
      } catch (error) {
        console.error('토큰 저장 중 오류:', error)
        setError('인증 처리 중 오류가 발생했어요.')
      }
    }

    handleAuthSuccess()
  }, [])

  // 단계별 진행 애니메이션
  useEffect(() => {
    if (error) return

    // Step 1: 즉시 완료
    setCurrentStep(1)

    // Step 2: 0.8초 후 완료
    const step2Timer = setTimeout(() => setCurrentStep(2), 800)

    // Step 3: 1.6초 후 완료
    const step3Timer = setTimeout(() => setCurrentStep(3), 1600)

    // 메시지 변경
    const msg1Timer = setTimeout(
      () => setLoadingMessage(LOADING_MESSAGES[1]),
      600,
    )
    const msg2Timer = setTimeout(
      () => setLoadingMessage(LOADING_MESSAGES[2]),
      1400,
    )

    // 2.5초 후 대시보드로 이동
    const redirectTimer = setTimeout(() => {
      window.location.href = '/'
    }, 2500)

    return () => {
      clearTimeout(step2Timer)
      clearTimeout(step3Timer)
      clearTimeout(msg1Timer)
      clearTimeout(msg2Timer)
      clearTimeout(redirectTimer)
    }
  }, [error])

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="max-w-md w-full mx-auto text-center">
          <div className="bg-surface-lowest rounded-lg shadow-sm border border-border p-8">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-16 w-16 text-error" />
            </div>

            <h1 className="text-xl font-bold text-on-surface mb-2">오류 발생</h1>

            <p className="text-on-surface-variant mb-6">{error}</p>

            <Button
              onClick={() => (window.location.href = '/')}
              className="w-full"
            >
              메인으로 돌아가기
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="max-w-md w-full mx-auto text-center">
        <div className="bg-surface-lowest rounded-lg shadow-sm border border-border p-8">
          {/* 제목 */}
          <h1 className="text-xl font-bold text-on-surface mb-6">
            {currentStep >= 3 ? '연동 완료!' : '연동 중...'}
          </h1>

          {/* 진행 단계 표시 */}
          <div className="space-y-3 mb-6">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex items-center justify-start px-4 py-2 rounded-lg transition-all duration-300 ${
                  currentStep >= step.id
                    ? 'bg-green-50'
                    : currentStep === step.id - 1
                      ? 'bg-primary/10'
                      : 'bg-surface'
                }`}
              >
                <div className="mr-3">
                  {currentStep >= step.id ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : currentStep === step.id - 1 ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  ) : (
                    <Circle className="h-5 w-5 text-outline-variant" />
                  )}
                </div>
                <span
                  className={`text-sm font-medium ${
                    currentStep >= step.id
                      ? 'text-green-700'
                      : currentStep === step.id - 1
                        ? 'text-primary'
                        : 'text-outline-variant'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          {/* 로딩 메시지 */}
          <p className="text-on-surface-variant text-sm h-5 transition-opacity duration-300">
            {currentStep < 3 ? loadingMessage : '대시보드로 이동합니다'}
          </p>
        </div>
      </div>
    </div>
  )
}
